import {
  CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum,
  CreateOrUpdateTransitOperationRequestDeclarationTypeEnum,
  CreateOrUpdateTransitOperationRequestSecurityEnum,
} from 'api/models'

const euCountries = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'ES', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'SE', 'XI'] as const
const eftaCtcCountries = ['CH', 'IS', 'LI', 'NO'] as const
const nonEuCtcCountries = ['AD', 'GB', 'SM', 'RS', 'MK', 'TR', 'UA'] as const
export const NctsCountries = [...euCountries, ...eftaCtcCountries, ...nonEuCtcCountries] as const
export type NctsCountry = typeof NctsCountries[number]

const {
  T1,
  T2,
  ENS,
} = CreateOrUpdateTransitOperationRequestDeclarationTypeEnum
const {
  S0,
  S1,
  S2,
  S3,
} = CreateOrUpdateTransitOperationRequestSecurityEnum
const {
  A,
  D,
} = CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum

type Environment = 'test' | 'live'
type PhaseId = 'P5' | 'P6'

type NctsCountryConfig = {
  declarationTypes: CreateOrUpdateTransitOperationRequestDeclarationTypeEnum[]
  securityTypes?: CreateOrUpdateTransitOperationRequestSecurityEnum[]
  additionalDeclarationTypes?: CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum[]
  environments?: Environment[]
  phase: PhaseId
  optIn?: boolean
  adminOnly?: boolean
  signedSubmission?: boolean
}

const NCTS_COUNTRY_CONFIGS: Map<NctsCountry, NctsCountryConfig> = new Map([
  ['EE', {
    declarationTypes: [T1], additionalDeclarationTypes: [A, D], phase: 'P6', optIn: true,
  }],
  ['FI', { declarationTypes: [T1, T2], phase: 'P5' }],
  ['LV', { declarationTypes: [T1], phase: 'P6', optIn: true }],
  ['RO', {
    declarationTypes: [T1], securityTypes: [S0, S2], additionalDeclarationTypes: [A, D], phase: 'P6',
  }],
  ['GB', { declarationTypes: [T1, T2], securityTypes: [S0, S2], phase: 'P6' }],
  ['PL', {
    declarationTypes: [T1], additionalDeclarationTypes: [A, D], phase: 'P6', optIn: true,
  }],
  ['SE', { declarationTypes: [T1], securityTypes: [S0, S2], phase: 'P6' }],
  ['LT', { declarationTypes: [T1], securityTypes: [S0, S2], phase: 'P6' }],
  ['HU', { declarationTypes: [T1], securityTypes: [S0, S2], phase: 'P5' }],
  ['UA', { declarationTypes: [T1], phase: 'P6', optIn: true }],
  ['BG', {
    declarationTypes: [T1, T2], phase: 'P6', optIn: true, signedSubmission: true,
  }],
  ['RS', {
    declarationTypes: [T1], additionalDeclarationTypes: [A, D], phase: 'P6', optIn: true, signedSubmission: true,
  }],
  ['SK', {
    declarationTypes: [T1], additionalDeclarationTypes: [A, D], phase: 'P6', optIn: true, signedSubmission: true,
  }],
])

const ENS_COUNTRY_CONFIGS: Map<NctsCountry, NctsCountryConfig> = new Map(
  [...euCountries, ...eftaCtcCountries].map((country) => [country, { declarationTypes: [ENS], phase: 'P6' as PhaseId }]), // tehniliselt ICS2 jaoks pole faasi
)

const COUNTRY_CONFIGS = [ENS_COUNTRY_CONFIGS, NCTS_COUNTRY_CONFIGS].reduce((acc, configs) => {
  configs.forEach((config, country) => {
    const existing = acc.get(country)
    if (existing) {
      acc.set(country, { ...existing, ...config, declarationTypes: [...existing.declarationTypes, ...config.declarationTypes] })
    } else {
      acc.set(country, config)
    }
  })
  return acc
}, new Map<NctsCountry, NctsCountryConfig>())

export const DEFAULT_TRANSIT_DECLARATION_TYPES = [T1, T2]

const isPhase6OptIn = (countryConfig: NctsCountryConfig): boolean => countryConfig.phase === 'P6' && !!countryConfig.optIn
const isPhase6OptOut = (countryConfig: NctsCountryConfig): boolean => countryConfig.phase === 'P6' && !countryConfig.optIn
const isAdminOnly = (countryConfig: NctsCountryConfig): boolean => !!countryConfig.adminOnly
const isSignedSubmission = (countryConfig: NctsCountryConfig): boolean => !!countryConfig.signedSubmission

export class TardekConfigProvider {
  private readonly countryConfigMap: Map<NctsCountry, NctsCountryConfig>

  // eslint-disable-next-line max-len
  private readonly countryDeclarationTypes$: Map<NctsCountry, CreateOrUpdateTransitOperationRequestDeclarationTypeEnum[]> = new Map<NctsCountry, CreateOrUpdateTransitOperationRequestDeclarationTypeEnum[]>()

  // eslint-disable-next-line max-len
  private readonly declarationTypeCountries$: Map<CreateOrUpdateTransitOperationRequestDeclarationTypeEnum, NctsCountry[]> = new Map<CreateOrUpdateTransitOperationRequestDeclarationTypeEnum, NctsCountry[]>()

  // eslint-disable-next-line max-len
  private readonly securityConfigurations$: Map<NctsCountry, CreateOrUpdateTransitOperationRequestSecurityEnum[]> = new Map<NctsCountry, CreateOrUpdateTransitOperationRequestSecurityEnum[]>()

  // eslint-disable-next-line max-len
  private readonly additionalDeclarationTypeConfigurations$: Map<NctsCountry, CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum[]> = new Map<NctsCountry, CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum[]>()

  private readonly phase6OptInCountries$: NctsCountry[] = []

  private readonly phase6OptOutCountries$: NctsCountry[] = []

  private readonly adminOnlyCountries$: NctsCountry[] = []

  private readonly signedSubmissionCountries$: NctsCountry[] = []

  constructor(countryConfigMap: Map<NctsCountry, NctsCountryConfig>) {
    this.countryConfigMap = countryConfigMap
    this.init()
  }

  get countryDeclarationTypes() {
    return this.countryDeclarationTypes$
  }

  get declarationTypeCountries() {
    return this.declarationTypeCountries$
  }

  get phase6OptInCountries() {
    return this.phase6OptInCountries$
  }

  get phase6OptOutCountries() {
    return this.phase6OptOutCountries$
  }

  get adminOnlyCountries() {
    return this.adminOnlyCountries$
  }

  get signedSubmissionCountries() {
    return this.signedSubmissionCountries$
  }

  isTransitCountry(country: NctsCountry | null) {
    if (!country) return false

    return this.phase6OptInCountries.includes(country) || this.phase6OptOutCountries.includes(country)
  }

  getCountries(predicate: (countryConfig: NctsCountryConfig) => boolean): NctsCountry[] {
    const countries : NctsCountry[] = []
    this.countryConfigMap.forEach((countryConfig, c) => {
      if (predicate(countryConfig)) countries.push(c)
    })

    return countries
  }

  securityConfigurationByCountry(country: NctsCountry): CreateOrUpdateTransitOperationRequestSecurityEnum[] {
    return this.securityConfigurations$.get(country) ?? [S0, S1, S2, S3]
  }

  // eslint-disable-next-line max-len
  additionalDeclarationTypeConfigurationByCountry(country: NctsCountry): CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum[] {
    return this.additionalDeclarationTypeConfigurations$.get(country) ?? [A]
  }

  private init() {
    this.countryConfigMap.forEach((countryConfig, country) => {
      const { declarationTypes, securityTypes, additionalDeclarationTypes } = countryConfig
      for (const declType of declarationTypes) {
        let declarationTypesPerCountry = this.countryDeclarationTypes$.get(country)
        if (!declarationTypesPerCountry) {
          declarationTypesPerCountry = []
          this.countryDeclarationTypes$.set(country, declarationTypesPerCountry)
        }
        declarationTypesPerCountry.push(declType)

        let countriesPerDeclarationType = this.declarationTypeCountries$.get(declType)
        if (!countriesPerDeclarationType) {
          countriesPerDeclarationType = []
          this.declarationTypeCountries$.set(declType, countriesPerDeclarationType)
        }
        countriesPerDeclarationType.push(country)
      }

      this.securityConfigurations$.set(country, securityTypes ?? [S0, S1, S2, S3])
      this.additionalDeclarationTypeConfigurations$.set(country, additionalDeclarationTypes ?? [A])

      if (isPhase6OptIn(countryConfig)) {
        this.phase6OptInCountries$.push(country)
      }
      if (isPhase6OptOut(countryConfig)) {
        this.phase6OptOutCountries$.push(country)
      }
      if (isAdminOnly(countryConfig)) {
        this.adminOnlyCountries$.push(country)
      }
      if (isSignedSubmission(countryConfig)) {
        this.signedSubmissionCountries$.push(country)
      }
    })
  }
}

export const tardekConfig = new TardekConfigProvider(COUNTRY_CONFIGS)

export const requiresSignedSubmission = (
  country: NctsCountry | null | undefined,
): boolean => !!country && tardekConfig.signedSubmissionCountries.includes(country)

export const createByEnvironmentFilter = (
  environment: Environment,
) => (countryConfig: NctsCountryConfig): boolean => {
  const environments: Environment[] = countryConfig.environments ?? ['test', 'live']

  return environments.includes(environment)
}

export const getSecurityConfiguration = (
  country: NctsCountry,
): CreateOrUpdateTransitOperationRequestSecurityEnum[] => tardekConfig.securityConfigurationByCountry(country)

export const getAdditionalDeclarationTypeConfiguration = (
  country: NctsCountry,
): CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum[] => tardekConfig
  .additionalDeclarationTypeConfigurationByCountry(country)

/**
 * A pre-lodged (D) declaration is lodged before the goods are presented, so the
 * declarant must additionally present the consignment (IE170) and may amend the
 * declaration while it is still only pre-lodged. Countries that only accept
 * standard (A) declarations have neither step.
 */
export const supportsPreLodgedDeclaration = (
  country: NctsCountry,
): boolean => getAdditionalDeclarationTypeConfiguration(country).includes(D)

export const getDeclarationTypeConfiguration = (
  country: NctsCountry,
): CreateOrUpdateTransitOperationRequestDeclarationTypeEnum[] => {
  if (!tardekConfig.countryDeclarationTypes.has(country)) {
    return [T1]
  }

  return tardekConfig.countryDeclarationTypes.get(country) ?? []
}

export const getTransitDeclarationTypeConfiguration = (
  country: NctsCountry,
): CreateOrUpdateTransitOperationRequestDeclarationTypeEnum[] => {
  if (!tardekConfig.countryDeclarationTypes.has(country)) {
    return [...DEFAULT_TRANSIT_DECLARATION_TYPES]
  }

  const countryTransitDeclarationTypes = tardekConfig.countryDeclarationTypes.get(country) ?? []
  const declarationTypes = countryTransitDeclarationTypes
    .filter((declType) => declType !== CreateOrUpdateTransitOperationRequestDeclarationTypeEnum.ENS)

  return declarationTypes.length ? declarationTypes : [...DEFAULT_TRANSIT_DECLARATION_TYPES]
}

export const isSupportedDeclarationTypeCountry = (
  country: NctsCountry,
  declarationType: CreateOrUpdateTransitOperationRequestDeclarationTypeEnum,
) => {
  const countries: NctsCountry[] = tardekConfig.declarationTypeCountries.get(declarationType) ?? []

  return countries.includes(country)
}
